import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Stack,
  Avatar,
  Button
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import PetsIcon from '@mui/icons-material/Pets';
import SmokeFreeIcon from '@mui/icons-material/SmokeFree';
import axios from 'axios';
import { KeyUri } from '../key';
import { VendorSidebar } from './VendorSidebar';
import { VendorHeader } from './VendorHeader';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton } from '@mui/material';

const vendorToken = localStorage.getItem('vendorToken') || null;

export function PropertyDetailPage({ propertyId, property, onBack }) {
  const navigate = useNavigate();
   const [propertyData, setPropertyData] = useState(null);
 // const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

useEffect(() => {
  async function fetchProperty() {
    const id = property?._id || propertyId;
    if (!id || !vendorToken) {
      setError('Missing property ID');
      setLoading(false);
      return;
    }

    try {
     
      const response = await axios.get(`${KeyUri.BACKEND_URI}/get-property/${id}`, {
        headers: { Authorization: `Bearer ${vendorToken}` },
      });
      
   
      setPropertyData(response.data.data);
      
    } catch (err) {
      setError('Failed to fetch property');
    } finally {
      setLoading(false);
    }
  }
  
  fetchProperty();
}, [property?._id, propertyId, vendorToken]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!propertyData) return <Typography>No property found.</Typography>;

  return (
    <Box>
       {/* Fixed header */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }}>
        <VendorHeader />
      </Box>
    <Box sx={{ maxWidth: 900, mx: 'auto', my: -3, p: 3 ,  }}>
 <Button
  variant="contained"
  startIcon={<ArrowBackIcon />}
  onClick={onBack}
  sx={{
    mb: 2,
    mr: 24,
    minWidth: 'auto',
    textTransform: 'none',
    fontWeight: 500,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    '&:hover': {
      bgcolor: 'action.hover',
      
      boxShadow: 1,
    },
  }}
>
  Back
</Button>


      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {propertyData.propertyName}
      </Typography>

      {/* Property Images */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          mb: 3,
          height: 240,
        }}
      >
        {(propertyData.images || []).map((img, idx) => (
          <Box
            key={idx}
            component="img"
            src={img.url}
            alt={`${propertyData.propertyName} image ${idx + 1}`}
            sx={{ borderRadius: 2, height: '100%', objectFit: 'cover', flexShrink: 0, width: 300 }}
          />
        ))}
      </Box>

      {/* Basic Information */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Basic Information
        </Typography>
         <Typography><strong>Name:</strong> {propertyData.propertyName}</Typography>
        <Typography><strong>Type:</strong> {propertyData.propertyType}</Typography>
        <Typography sx={{ mt: 1 }}>{propertyData.description}</Typography>
         <Typography sx={{ mt: 1 }}><strong>Year of Established:</strong>{propertyData.yearEstablished}</Typography>
         <Typography sx={{ mt: 1 }}><strong>Total Area:</strong> {propertyData.totalArea}</Typography>
         <Typography sx={{ mt: 1 }}><strong>Number of Floors:</strong> {propertyData.numberOfFloors}</Typography>
           <Typography sx={{ mt: 1 }}><strong>Check In Time:</strong> {propertyData.checkInTime}</Typography>
             <Typography sx={{ mt: 1 }}><strong>Check Out Time:</strong> {propertyData.checkOutTime}</Typography>
        <Typography sx={{ mt: 1 }}>
          <strong>Status:</strong>{' '}
          <Chip
            label={propertyData.status?.charAt(0).toUpperCase() + propertyData.status?.slice(1)}
            color={
              propertyData.status === 'approved'
                ? 'success'
                : propertyData.status === 'pending'
                ? 'warning'
                : 'default'
            }
            variant={propertyData.status === 'draft' ? 'outlined' : 'filled'}
          />
        </Typography>
      </Card>

      {/* Location */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Location
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <LocationOnIcon color="action" />
         <Typography>
  {propertyData.location?.address}, {propertyData.location?.city}, {propertyData.location?.state} - {propertyData.location?.pincode}
</Typography>

        </Stack>
        {propertyData.latitude && propertyData.longitude && (
          <Typography variant="body2" color="text.secondary">
            Coordinates: {propertyData.latitude}, {propertyData.longitude}
          </Typography>
        )}
      </Card>

      {/* Contact */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Contact Information
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
<Typography><strong>Contact Person:</strong> {propertyData.contact?.person}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <EmailIcon color="action" />
<Typography>{propertyData.contact?.email}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <PhoneIcon color="action" />
          <Typography>{propertyData.contact?.phone}</Typography>
        </Stack>
      </Card>

  {/* Accommodations */}
<Card sx={{ p: 3, mb: 3 }}>
  <Typography variant="h6" fontWeight="bold" gutterBottom>
    Rooms & Accommodations ({propertyData.accommodations?.length || 0})
  </Typography>
  
  {(!propertyData.accommodations?.length) ? (
    <Typography color="text.secondary">No accommodations listed.</Typography>
  ) : (
    <List>
      {propertyData.accommodations.map((acc, i) => (
        <ListItem key={i} divider sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="600">
            {acc.name} ({acc.type})
          </Typography>
          {/* SIZE */}
          <Typography variant="body2">
            Size: {typeof acc.size === 'object' 
              ? `${acc.size?.width || ''}×${acc.size?.length || ''} ft (${acc.size?.area || 'N/A'} sq ft)`
              : `${acc.size || 'N/A'} sq ft`
            }
          </Typography>

          {/* OCCUPANCY - HOSTEL SUPPORT */}
          <Typography variant="body2" color="primary">
            {acc.type === 'dormitory' 
              ? `${acc.bedsPerRoom || acc.maxOccupancy} Beds • ${acc.genderRestriction || 'Any'}`
              : `${acc.maxOccupancy} guests`
            }
          </Typography>
          <Typography variant="body2">Occupancy: {acc.maxOccupancy}</Typography>
          <Typography variant="body2">
            Price: ₹{acc.pricePerNight || acc.pricePerNightB2C || 'N/A'} (per night)
          </Typography>
          {['CP', 'MAP', 'AP'].some(plan => acc.mealPlanPricing?.[plan]?.B2C) && (
  <Box sx={{ mt: 0.5 }}>
    <Typography variant="body2" fontWeight={600}>Meal Plans:</Typography>
    {[
      { key: 'CP',  label: 'CP — Room + Breakfast' },
      { key: 'MAP', label: 'MAP — Room + Breakfast + Dinner' },
      { key: 'AP',  label: 'AP — Room + All Meals' },
    ].map(({ key, label }) =>
      acc.mealPlanPricing?.[key]?.B2C ? (
        <Typography key={key} variant="body2" color="text.secondary">
          {label}: B2C ₹{acc.mealPlanPricing[key].B2C} / B2B ₹{acc.mealPlanPricing[key].B2B || 'N/A'} per night
        </Typography>
      ) : null
    )}
  </Box>
)}
          {acc.description && (
            <Typography variant="body2" color="text.secondary">{acc.description}</Typography>
          )}
          <Typography variant="body2">Available: {acc.availableQuantity || acc.quantity || 0}</Typography>
          <Typography variant="body2">Beds: {acc.bedConfiguration}</Typography>
          
          {acc.amenities?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Amenities:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {acc.amenities.map((amenity, idx) => (
                  <Chip key={idx} label={amenity} size="small" />
                ))}
              </Stack>
            </Box>
          )}
          
          {acc.images?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, overflowX: 'auto' }}>
              {acc.images.map((img, idx) => (
                <Box
                  key={idx}
                  component="img"
                  src={img.url}
                  alt={`${acc.name} image ${idx + 1}`}
                  sx={{ height: 80, width: 80, objectFit: 'cover', borderRadius: 1 }}
                />
              ))}
            </Box>
          )}
        </ListItem>
      ))}
    </List>
  )}
</Card>


      {/* Amenities */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Amenities
        </Typography>
        {['general', 'recreational', 'dining', 'services'].map((category) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" sx={{ textTransform: 'capitalize' }}>
              {category}:
            </Typography>
            {propertyData.amenities && propertyData.amenities[category]?.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {propertyData.amenities[category].map((amenity, idx) => (
                  <Chip key={idx} label={amenity} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No {category} amenities listed.
              </Typography>
            )}
          </Box>
        ))}
      </Card>

      {/* Policies */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Policies
        </Typography>
        <Typography>
          <strong>Cancellation Policy:</strong> {propertyData.policies.cancellation || 'Not specified'}
        </Typography>
        <Typography>
          <strong>Pet Policy:</strong> {propertyData.policies.petPolicy || 'Not specified'}
        </Typography>
        <Typography>
          <strong>Smoking Policy:</strong> {propertyData.policies.smokingPolicy || 'Not specified'}
        </Typography>
        <Typography>
          <strong>Child Policy:</strong> {propertyData.policies.childPolicy || 'Not specified'}
        </Typography>
      </Card>

      {/* Languages */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Languages Spoken
        </Typography>
        {propertyData.languages && propertyData.languages.length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {propertyData.languages.map((lang, idx) => (
              <Chip key={idx} label={lang} />
            ))}
          </Stack>
        ) : (
          <Typography>No language information available.</Typography>
        )}
      </Card>

       <Card sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight="bold" gutterBottom>
          Accessibility
        </Typography>
        {propertyData.accessibility && propertyData.accessibility.length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {propertyData.accessibility.map((lang, idx) => (
              <Chip key={idx} label={lang} />
            ))}
          </Stack>
        ) : (
          <Typography>No language information available.</Typography>
        )}
         <Typography marginTop={2}>
          <strong>Special Features:</strong> {propertyData.specialFeatures || 'Not specified'}
        </Typography>
        <Typography marginTop={2}>
          <strong>Near by Attractions:</strong> {propertyData.nearbyAttractions || 'Not specified'}
        </Typography>
      </Card>
    </Box>
    </Box>
  );
}
